export type VeeraContextualThreadRole = "leader" | "coach";

export type VeeraContextualThreadEntrySurface =
  | "today"
  | "insights"
  | "reflection"
  | "leader_card"
  | "post_meeting_review"
  | "portfolio";

export type VeeraContextualThreadSourceRef = string | null | undefined;

export type VeeraContextualThreadSourceMatchStrategy = "semantic" | "strict";

export interface VeeraContextualThreadInput {
  role: VeeraContextualThreadRole;
  entrySurface: VeeraContextualThreadEntrySurface;
  primaryEntityId: string | null;
  topicFingerprint: string;
  contextualTopicId?: string | null;
  sourceId?: VeeraContextualThreadSourceRef;
}

export interface VeeraContextualThreadRecord extends VeeraContextualThreadInput {
  id: string;
  threadKey: string;
}

export interface VeeraContextualThreadMatchResult<TThread extends VeeraContextualThreadRecord> {
  thread: TThread | null;
  reason: string | null;
}

function normalizeVeeraThreadToken(value: string | null | undefined) {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return "unknown";
  return normalized
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-") || "unknown";
}

function normalizeVeeraContextualTopicId(value: string | null | undefined) {
  const normalized = value?.trim();
  if (!normalized) return null;
  const parts = normalized.split("|").map((part) => normalizeVeeraThreadToken(part));
  return parts.every((part) => part === "unknown") ? null : parts.join("|");
}

export function buildVeeraContextualTopicId(input: Pick<
  VeeraContextualThreadInput,
  "role" | "entrySurface" | "primaryEntityId" | "topicFingerprint" | "contextualTopicId"
>) {
  const explicitTopicId = normalizeVeeraContextualTopicId(input.contextualTopicId);
  if (explicitTopicId) {
    return explicitTopicId;
  }

  return [
    normalizeVeeraThreadToken(input.role),
    normalizeVeeraThreadToken(input.entrySurface),
    normalizeVeeraThreadToken(input.primaryEntityId),
    normalizeVeeraThreadToken(input.topicFingerprint),
  ].join("|");
}

export function buildVeeraContextualThreadKey(input: VeeraContextualThreadInput) {
  return [
    buildVeeraContextualTopicId(input),
    normalizeVeeraThreadToken(input.sourceId),
  ].join("|");
}

export function matchExistingVeeraThread<TThread extends VeeraContextualThreadRecord>(params: {
  threads: TThread[];
  candidate: VeeraContextualThreadInput;
  sourceMatchStrategy?: VeeraContextualThreadSourceMatchStrategy;
}): VeeraContextualThreadMatchResult<TThread> {
  const sourceMatchStrategy = params.sourceMatchStrategy ?? "semantic";
  const candidateMatchKey = sourceMatchStrategy === "strict"
    ? buildVeeraContextualThreadKey(params.candidate)
    : buildVeeraContextualTopicId(params.candidate);
  const matched = params.threads.find((thread) => (
    sourceMatchStrategy === "strict"
      ? buildVeeraContextualThreadKey(thread) === candidateMatchKey
      : buildVeeraContextualTopicId(thread) === candidateMatchKey
  ));

  if (!matched) {
    return {
      thread: null,
      reason: null,
    };
  }

  const reasonParts = [
    `strategy=${sourceMatchStrategy}`,
    `role=${normalizeVeeraThreadToken(params.candidate.role)}`,
    `surface=${normalizeVeeraThreadToken(params.candidate.entrySurface)}`,
    `primaryEntityId=${normalizeVeeraThreadToken(params.candidate.primaryEntityId)}`,
    `topicFingerprint=${normalizeVeeraThreadToken(params.candidate.topicFingerprint)}`,
  ];

  if (params.candidate.sourceId !== undefined && params.candidate.sourceId !== null) {
    reasonParts.push(`sourceId=${normalizeVeeraThreadToken(params.candidate.sourceId)}`);
  }

  return {
    thread: matched,
    reason: `Matched ${reasonParts.join(", ")}.`,
  };
}
